import { FilterOutlined, LoadingOutlined } from '@ant-design/icons'
import { usePageTitle, useView } from '@hooks';
import { Button, Checkbox, Empty, Spin, Table } from 'antd'
import { ExportDropdown } from './ExportDropdown'
import { ExpandableConfig } from 'antd/lib/table/interface'
import { SizeType } from 'antd/lib/config-provider/SizeContext'
import React, { useEffect, useRef, useState } from 'react'
import './SharedTable.css';

interface IProps {
  loading?: boolean;
  children?: any;
  emptyText?: React.ReactNode | (() => React.ReactNode);
  columns?: any;
  maxResultCount?: number;
  dataSource: any[];
  totalCount: number;
  sizeType?: SizeType;
  currentPage?: number;
  handlePageChange?: (pagination: any) => void;
  summary?: ((data: readonly any[]) => React.ReactNode) | undefined;
  enableRowClick?: boolean;
  onRowClick?: (record: any) => void;
  bottomMargin?: number;
  expandable?: ExpandableConfig<any>;
  mobileFilters?: React.ReactNode;
  exportable?: boolean;
  exportFileName?: string;
  exportColumns?: any[];
  onExportAll?: () => Promise<any[]>;
  mobileSelectable?: boolean;
  selectable?: boolean;
  selectedIds?: string[];
  onSelectChange?: (checked: boolean, record: any) => void;
  isSelectDisabled?: (record: any) => boolean;
  headerActions?: React.ReactNode;
  scrollX?: number | string | true;
  handleSortChange?: (sorting?: string) => void;
  /** `false` for a list that is always a handful of rows, where a pager is only clutter. */
  pagination?: false;
  /** The page draws its own pager (in the SummaryBar), so the table leaves its own out. */
  externalPagination?: boolean;
}

const SORT_DIRECTION: Record<string, string> = { ascend: 'ASC', descend: 'DESC' };

/**
 * Turns antd's sorter payload into the ABP `sorting` string the paged endpoints take.
 *
 * The field comes from the column's `dataIndex`, or from its `key` when the column renders
 * off the whole record and has none. Either way it is camelCase while the entity property is
 * PascalCase, so the first letter is upper-cased - the endpoints that still hand `sorting` to
 * dynamic LINQ need the property spelled exactly.
 */
const toSortingParam = (sorter: any): string | undefined => {
  const active = Array.isArray(sorter) ? sorter.find((item) => item?.order) : sorter;
  const direction = SORT_DIRECTION[active?.order];
  if (!direction) return undefined;

  const field = active.field ?? active.columnKey;
  if (typeof field !== 'string' || !field) return undefined;

  return `${field.charAt(0).toUpperCase()}${field.slice(1)} ${direction}`;
};

export const SharedTable: React.FC<IProps> = ({
  loading,
  children,
  columns,
  maxResultCount = 15,
  dataSource,
  totalCount,
  emptyText = <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />,
  currentPage,
  summary,
  handlePageChange,
  sizeType = 'small',
  enableRowClick = false,
  onRowClick,
  bottomMargin = 30,
  expandable,
  mobileFilters,
  exportable = false,
  exportFileName = 'export',
  exportColumns,
  onExportAll,
  mobileSelectable = false,
  selectable = false,
  selectedIds = [],
  onSelectChange,
  isSelectDisabled,
  headerActions,
  scrollX = true,
  handleSortChange,
  pagination,
  externalPagination = false,
}) => {
  const { isMobile } = useView();
  const [scrollY, setScrollY] = useState<number | undefined>(undefined);
  // The whole table block's height when it has the room to itself, so that a short page of rows
  // still reaches the bottom and the bar under it stays put instead of riding up with the rows.
  const [fillHeight, setFillHeight] = useState<number | undefined>(undefined);
  const tableRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
  const scrollTop = sessionStorage.getItem('tableScrollTop');
  if (scrollTop && tableRef.current) {
    const scrollContainer = tableRef.current.querySelector('.ant-table-body') as HTMLElement;
    if (scrollContainer) {
      scrollContainer.scrollTop = parseInt(scrollTop, 10);
    }
  }
}, [dataSource]);

  useEffect(() => {
    const updateHeight = () => {
      if (!containerRef.current) return;
      
      const rect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const availableHeight = viewportHeight - rect.top - bottomMargin;
      
      const tableElement = containerRef.current.querySelector('.ant-table');
      if (!tableElement) return;
      
      const tableHeader = tableElement.querySelector('.ant-table-thead') as HTMLElement;
      const tablePagination = containerRef.current.querySelector('.ant-pagination') as HTMLElement;
      const tableSummary = tableElement.querySelector('.ant-table-summary') as HTMLElement;
      
      const headerHeight = tableHeader?.offsetHeight || 0;
      const paginationHeight = tablePagination?.offsetHeight || 0;
      const summaryHeight = tableSummary?.offsetHeight || 0;
      
      const extraSpacing = 35;
      
      const calculatedScrollY = availableHeight - headerHeight - paginationHeight - summaryHeight - extraSpacing;
      
      const minHeight = 200;
      const nextScrollY = Math.max(calculatedScrollY, minHeight);
      // Sub-pixel churn would re-render the table for nothing and shows up as a flicker.
      setScrollY((prev) => (prev !== undefined && Math.abs(prev - nextScrollY) < 1 ? prev : nextScrollY));
      const nextFill = Math.round(nextScrollY + headerHeight + summaryHeight);
      setFillHeight((prev) => (prev !== undefined && Math.abs(prev - nextFill) < 1 ? prev : nextFill));
    };

    // Measured again once the pagination and summary widgets have settled - they can grow a
    // line later. The guard above keeps that second pass silent unless the height really moved.
    const timeoutId = setTimeout(updateHeight, 100);

    updateHeight();
    window.addEventListener('resize', updateHeight);

    return () => {
      window.removeEventListener('resize', updateHeight);
      clearTimeout(timeoutId);
    };
  }, [bottomMargin, dataSource.length, totalCount]);

  // Sorting is served by the API, so a sort click is routed to the hook rather than to the
  // page handler - it re-queries from page one instead of re-ordering the loaded page.
  const handleTableChange = (pagination: any, _filters: any, sorter: any, extra: any) => {
    if (extra?.action === 'sort') {
      handleSortChange?.(toSortingParam(sorter));
      return;
    }
    handlePageChange?.(pagination);
  };

  const handleRowClick = (record: any) => {
    const scrollContainer = tableRef.current?.querySelector('.ant-table-body') as HTMLElement;
    const scrollTop = scrollContainer?.scrollTop ?? 0;
    sessionStorage.setItem('tableScrollTop', scrollTop.toString());
    if (onRowClick) {
      onRowClick(record);
    }
  };

  const [showFilters, setShowFilters] = useState(false);

  const { setExtraHeaderActions } = usePageTitle();

  // Push filter button + export button into header
  const hasMobileFilters = !!mobileFilters;
  const exportCols = exportColumns || columns;

  // Stable key: only re-run when actual data IDs change, not on every render
  const dataKey = dataSource.map((r) => r.id).join(',');

  useEffect(() => {
    const actions: React.ReactNode[] = [];

    if (headerActions) {
      actions.push(<React.Fragment key="table-header-actions">{headerActions}</React.Fragment>);
    }

    if (exportable) {
      actions.push(
        <ExportDropdown
          key="table-export"
          columns={exportCols || []}
          dataSource={dataSource}
          fileName={exportFileName}
          onFetchAll={onExportAll}
        />
      );
    }

    if (isMobile && hasMobileFilters) {
      actions.push(
        <Button
          key="table-filter"
          icon={<FilterOutlined />}
          onClick={() => setShowFilters((prev) => !prev)}
          style={{ background: '#52c41a', borderColor: '#52c41a', color: '#fff' }}
        />
      );
    }

    // Only a table that put something in the header slot clears it: an embedded table (a drawer
    // on the order page) must not wipe the actions the page itself placed there.
    if (actions.length === 0) return undefined;

    setExtraHeaderActions(<>{actions}</>);
    return () => setExtraHeaderActions(null);
  }, [isMobile, hasMobileFilters, exportable, exportFileName, dataKey, headerActions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Accumulate items for mobile load-more
  const [accumulatedData, setAccumulatedData] = useState<any[]>([]);
  const prevDataRef = useRef<string>('');

  useEffect(() => {
    if (!isMobile) return;
    const ids = dataSource.map((r) => r.id).join(',');

    // The first page is the source of truth, so it always replaces the list - a row edited in
    // place keeps its id, and the id check below would otherwise leave its card stale.
    if ((currentPage ?? 1) === 1) {
      prevDataRef.current = ids;
      setAccumulatedData(dataSource);
      return;
    }

    if (ids === prevDataRef.current) return;
    prevDataRef.current = ids;

    setAccumulatedData((prev) => {
      const existingIds = new Set(prev.map((r) => r.id));
      const newItems = dataSource.filter((r) => !existingIds.has(r.id));
      return [...prev, ...newItems];
    });
  }, [isMobile, dataSource, currentPage]);

  const hasMore = accumulatedData.length < totalCount;
  const handleLoadMore = () => {
    const nextPage = (currentPage ?? 1) + 1;
    handlePageChange?.({ current: nextPage, pageSize: maxResultCount });
  };

  if (isMobile) {
    const visibleColumns = (columns || []).filter((col: any) => col.key !== 'checkbox');

    return (
      <div className="shared-table-container">
        {/* Collapsible filters */}
        {showFilters && mobileFilters && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 8,
            padding: '10px 12px',
            marginBottom: 8,
            background: '#fafafa',
            borderRadius: 8,
            border: '1px solid #f0f0f0',
          }}>
            {mobileFilters}
          </div>
        )}

        <Spin spinning={!!loading && accumulatedData.length === 0} indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}>
          {accumulatedData.length === 0 && !loading ? (
            <div style={{ padding: 24 }}>{typeof emptyText === 'function' ? emptyText() : emptyText}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {accumulatedData.map((record: any) => (
                <div
                  key={record.id}
                  onClick={(e) => {
                    const target = e.target as HTMLElement;
                    if (target.closest('.ant-checkbox-wrapper') || target.closest('.ant-checkbox')) return;
                    if (enableRowClick && onRowClick) onRowClick(record);
                  }}
                  style={{
                    background: mobileSelectable && selectedIds.includes(record.id) ? '#f0f7ff' : '#fff',
                    border: mobileSelectable && selectedIds.includes(record.id) ? '1px solid #2CBDFF' : '1px solid #d9d9d9',
                    borderRadius: 8,
                    padding: '10px 14px',
                    cursor: enableRowClick ? 'pointer' : undefined,
                    display: 'flex',
                    gap: mobileSelectable ? 10 : 0,
                    alignItems: 'flex-start',
                  }}
                >
                  {mobileSelectable && onSelectChange && (
                    <Checkbox
                      checked={selectedIds.includes(record.id)}
                      disabled={isSelectDisabled?.(record)}
                      onChange={(e) => {
                        e.stopPropagation();
                        onSelectChange(e.target.checked, record);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      style={{ marginTop: 2 }}
                    />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                  {visibleColumns.map((col: any, idx: number) => {
                    const value = col.dataIndex ? record[col.dataIndex] : record;
                    const rendered = col.render ? col.render(value, record, idx) : value;
                    if (rendered == null || rendered === '') return null;
                    return (
                      <div
                        key={col.key || col.dataIndex || idx}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '3px 0',
                          ...(idx === 0 ? { marginBottom: 4 } : {}),
                        }}
                      >
                        {col.title && (
                          <span style={{ fontSize: 11, color: '#8c8c8c', textTransform: 'uppercase', flexShrink: 0, marginRight: 8 }}>
                            {col.title}
                          </span>
                        )}
                        <span style={{ fontSize: 13, textAlign: 'right', flex: 1, minWidth: 0 }}>{rendered}</span>
                      </div>
                    );
                  })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Spin>

        {/* Load more */}
        {hasMore && (
          <Button
            block
            type="text"
            loading={loading}
            onClick={handleLoadMore}
            style={{ marginTop: 8, color: '#2CBDFF', fontWeight: 500 }}
          >
            Load more ({accumulatedData.length} of {totalCount})
          </Button>
        )}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`shared-table-container${scrollX !== true ? ' nowrap-head' : ''}`}
    >
      <div ref={tableRef} style={externalPagination ? { minHeight: fillHeight } : undefined}>
        <Table
          size={sizeType}
          locale={{ emptyText: loading ? 'Loading...' : emptyText }}
          rowKey={(record) => record.id.toString()}
          columns={columns}
          pagination={
            pagination === false || externalPagination
              ? false
              : {
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '15', '20', '30', '50', '100'],
                  pageSize: maxResultCount,
                  total: totalCount,
                  defaultCurrent: 1,
                  current: currentPage,
                  size: 'middle',
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                }
          }
          loading={{
            spinning: loading,
            tip: 'Loading...',
            indicator: <LoadingOutlined style={{ fontSize: 24 }} spin />,
          }}
          dataSource={dataSource}
          scroll={{ y: scrollY, x: scrollX }}
          rowSelection={
            selectable
              ? {
                  selectedRowKeys: selectedIds,
                  preserveSelectedRowKeys: true,
                  hideSelectAll: true,
                  onSelect: (record: any, checked: boolean) => onSelectChange?.(checked, record),
                  getCheckboxProps: (record: any) => ({
                    disabled: isSelectDisabled?.(record),
                  }),
                }
              : undefined
          }
          onChange={handleTableChange}
          summary={summary}
          expandable={expandable}
          onRow={
            enableRowClick
              ? (record) => ({
                  onClick: (event) => {
                    const target = event.target as HTMLElement;
                    if (target.closest('.ant-checkbox-wrapper') || target.closest('.ant-checkbox')) {
                      event.stopPropagation();
                      return;
                    }
                    // Let the expand caret toggle the row without also firing the row-open action.
                    if (target.closest('.ant-table-row-expand-icon')) {
                      return;
                    }
                    handleRowClick(record);
                  },
                  style: { cursor: 'pointer' },
                })
              : undefined
          }
        >
          {children}
        </Table>
      </div>
    </div>
  );
};